/**
 * Sketch saving functionality for TokoWrapper
 *
 * Provides functions to save sketches as images (PNG) or SVG files,
 * and to save sketch settings as JSON files.
 *
 * @namespace SaveSketch
 */

import { libraryState } from '../core/state';
import { _stateToPreset } from '../ui/tweakpaneUtil';

/**
 * Save the current sketch as an image file
 * Automatically detects whether the sketch is canvas or SVG and saves accordingly
 * @returns {string|undefined} The filename of the saved file, or undefined if save failed
 */
export function saveSketch () {
  //
  // detect if the sketch is in canvas or svg
  //
  let isCanvas = null;
  let isSVG = null;

  let sketchContainer = document.getElementById(libraryState.options.sketchElementId);
  if (!sketchContainer || !sketchContainer.firstChild) {
    console.warn('Toko - saveSketch: sketch element not found');
    return;
  }
  let sketchElement = sketchContainer.firstChild;
  isCanvas = sketchElement instanceof HTMLCanvasElement;
  if (sketchElement.firstChild != null) {
    isSVG = sketchElement.firstChild.nodeName == 'svg';
  }

  if (isCanvas) {
    //
    //  save canvas as png
    //
    let filename = libraryState.toko.generateFilename('png');
    const saveCanvasFn = libraryState.p5Canvas?.saveCanvas ?? (typeof saveCanvas === 'function' ? saveCanvas : null);
    if (typeof saveCanvasFn !== 'function') {
      console.warn('Toko - saveSketch: saveCanvas is not available');
      return;
    }
    saveCanvasFn.call(libraryState.p5Canvas ?? window, filename, 'png');
    return filename;
  } else if (isSVG) {
    //
    // add attributes to ensure proper preview of the SVG file in the Finder
    //
    const svgElement = sketchContainer.querySelector('svg');
    if (!svgElement) {
      console.warn('Toko - saveSketch: SVG element not found');
      return;
    }
    svgElement.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    let filename = libraryState.toko.generateFilename('svg');
    let svgString = svgElement.outerHTML;

    let blob = new Blob([svgString], { type: 'image/svg+xml' });
    let url = window.URL.createObjectURL(blob);

    //
    // create a hidden url with the image and click it
    //
    var a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);

    return filename;
  } else {
    console.warn('Toko - saveSketch: unknown type');
    return;
  }
}

/**
 * Save both the sketch image and its settings
 * @returns {string|undefined} The base filename (without extension) of the saved files
 */
export function saveSketchAndSettings () {
  let filename = saveSketch();
  if (!filename || typeof filename !== 'string') {
    return;
  }
  //
  //  strip the extension of the filename so we can reuse it.
  //
  filename = filename.split('.').slice(0, -1).join('.');
  saveSettings(filename);
}

/**
 * Save the current sketch settings from Tweakpane as a JSON file
 * @param {string} [filename='default'] - Base filename for the settings file
 */
export function saveSettings (filename = 'default') {
  // determine the filename
  if (typeof filename === 'undefined' || filename === 'default') {
    filename = libraryState.toko.generateFilename('json');
  }

  // add extension if needed
  if (!filename.endsWith('.json')) {
    filename += '.json';
  }

  // gather the current Tweakpane state
  const state = libraryState.tweakpane.base.exportState();
  const settings = _stateToPreset(state);

  // save with p5.js native saver
  const saveJsonFn = libraryState.p5Canvas?.saveJSON ?? (typeof saveJSON === 'function' ? saveJSON : null);
  if (typeof saveJsonFn !== 'function') {
    console.warn('Toko - saveSettings: saveJSON is not available');
    return;
  }
  saveJsonFn.call(libraryState.p5Canvas ?? window, settings, filename);
}
